import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { InventoryHistory } from 'types/inventory';
import Button from 'components/Button';
import Toast from 'components/Toast';
import styles from './InventoryReport.module.scss';
import { ArrowLeft, Package, X, Search, FileText } from 'lucide-react';
import { generateInventoryReportPDF } from './generatePDF';
import { useReportHeaderMetadata } from '../useReportHeaderMetadata';
import { EditableReportTitleHeading } from '../EditableReportTitleHeading';
import { isReportHeaderValuePresent } from '../reportHeader';

function InventoryReport() {
  const defaultReportTitle = 'Inventory Report';
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InventoryHistory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  );
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [downloading, setDownloading] = useState(false);

  const {
    reportTitle,
    setReportTitle,
    resolvedReportTitle,
    persistedTitle,
    persistReportTitle,
    canSaveTitle,
    isSavingTitle,
    isReportTitleEditable,
    companyHeader,
  } = useReportHeaderMetadata(defaultReportTitle, 'inventory-report');

  const loadReport = useCallback(async () => {
    if (!apiClient.companyId) return;
    setLoading(true);
    setError(null);
    try {
      const reportData = await apiClient.get<InventoryHistory[]>(
        API_ROUTES.INVENTORY_HISTORY,
      );
      setData(reportData);
      setError(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load report';
      setError(errorMessage);
      setData([]);
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (!apiClient.isCompanyInitialized) {
      setLoading(false);
      return;
    }
    void loadReport();
  }, [loadReport, apiClient.isCompanyInitialized]);

  // Get unique products from the data
  const uniqueProducts = useMemo(() => {
    const productMap = new Map<string, { id: string; name: string }>();
    data.forEach((entry) => {
      if (entry.product.id && !productMap.has(entry.product.id)) {
        productMap.set(entry.product.id, {
          id: entry.product.id,
          name: entry.product.name || 'Unknown Product',
        });
      }
    });
    return Array.from(productMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [data]);

  // Filter products based on search query (exclude already selected)
  const filteredProducts = useMemo(() => {
    const availableProducts = uniqueProducts.filter(
      (product) => !selectedProductIds.has(product.id),
    );
    if (!productSearchQuery.trim()) {
      return availableProducts;
    }
    const query = productSearchQuery.toLowerCase();
    return availableProducts.filter((product) =>
      product.name.toLowerCase().includes(query),
    );
  }, [uniqueProducts, productSearchQuery, selectedProductIds]);

  // Filter data by selected products
  const filteredData = useMemo(() => {
    if (selectedProductIds.size === 0) {
      return data;
    }
    return data.filter((entry) => selectedProductIds.has(entry.product.id));
  }, [data, selectedProductIds]);

  // Get selected products
  const selectedProducts = useMemo(() => {
    return uniqueProducts.filter((product) =>
      selectedProductIds.has(product.id),
    );
  }, [uniqueProducts, selectedProductIds]);

  const handleSelectProduct = useCallback((productId: string) => {
    setSelectedProductIds((prev) => new Set(prev).add(productId));
    setProductSearchQuery('');
    setIsSearchFocused(false);
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSelectedProductIds(new Set());
    setProductSearchQuery('');
    setIsSearchFocused(false);
  }, []);

  const handleDownloadPDF = () => {
    if (!data) return;

    setDownloading(true);
    try {
      generateInventoryReportPDF(
        filteredData,
        false,
        'arial',
        resolvedReportTitle,
        companyHeader,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString: string): { date: string; time: string } => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { date: 'Invalid Date', time: '' };
      }

      // Format date as dd/mm/yyyy
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;

      // Format time as hh:mm:ss AM/PM
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const formattedTime = `${String(displayHours).padStart(
        2,
        '0',
      )}:${minutes}:${seconds} ${ampm}`;

      return { date: formattedDate, time: formattedTime };
    } catch {
      return { date: 'Invalid Date', time: '' };
    }
  };

  const getChangeTypeLabel = (changeType: string): string => {
    switch (changeType.toLowerCase()) {
      case 'add':
        return 'Add';
      case 'remove':
        return 'Remove';
      case 'sale':
        return 'Sale';
      default:
        return changeType;
    }
  };

  const getChangeTypeClass = (changeType: string): string => {
    switch (changeType.toLowerCase()) {
      case 'add':
        return styles.changeTypeAdd;
      case 'remove':
        return styles.changeTypeRemove;
      case 'sale':
        return styles.changeTypeSale;
      default:
        return styles.changeTypeDefault;
    }
  };

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.REPORTS.ROOT)}
            aria-label="Back to reports"
          >
            <ArrowLeft size={16} />
            Back to Reports
          </Button>
          <h1 className={styles.title}>Inventory Report</h1>
        </div>
        {!loading && data.length > 0 && (
          <div className={styles.headerActions}>
            <Button
              onClick={() => void handleDownloadPDF()}
              variant="outline"
              disabled={downloading}
              aria-label="Download PDF"
            >
              <FileText size={16} />
              {downloading ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>Loading inventory history...</div>
        ) : error ? (
          <div className={styles.error}>
            <p>{error}</p>
            <Button onClick={() => void loadReport()}>Retry</Button>
          </div>
        ) : data.length === 0 ? (
          <div className={styles.emptyState}>
            <Package size={48} className={styles.emptyIcon} />
            <p>No inventory history found</p>
          </div>
        ) : (
          <div className={styles.reportContent}>
            <div className={styles.period}>
              {isReportHeaderValuePresent(
                companyHeader.registeredBusinessName,
              ) && (
                <>
                  <strong>{companyHeader.registeredBusinessName}</strong>
                  <br />
                </>
              )}
              {isReportHeaderValuePresent(
                companyHeader.registeredBusinessAddress,
              ) && (
                <>
                  {companyHeader.registeredBusinessAddress}
                  <br />
                </>
              )}
              {isReportHeaderValuePresent(
                companyHeader.registeredBusinessTin,
              ) && (
                <>
                  <strong>TIN:</strong> {companyHeader.registeredBusinessTin}
                  <br />
                </>
              )}
              <EditableReportTitleHeading
                as="strong"
                titleClassName={styles.reportEmbeddedTitle}
                resolvedTitle={resolvedReportTitle}
                value={reportTitle}
                defaultTitle={defaultReportTitle}
                persistedTitle={persistedTitle}
                onChange={setReportTitle}
                onSave={persistReportTitle}
                canSave={canSaveTitle}
                saving={isSavingTitle}
                editable={isReportTitleEditable}
              />
            </div>

            {/* Product Filter */}
            <div className={styles.filterSection}>
              <label className={styles.filterLabel} htmlFor="product-filter">
                Filter by Product:
              </label>
              <div className={styles.productSearch}>
                {/* Selected Products Tags */}
                {selectedProducts.length > 0 && (
                  <div className={styles.selectedProductsTags}>
                    {selectedProducts.map((product) => (
                      <div key={product.id} className={styles.productTag}>
                        <span className={styles.productTagName}>
                          {product.name}
                        </span>
                        <button
                          type="button"
                          className={styles.removeTagButton}
                          onClick={() => handleRemoveProduct(product.id)}
                          aria-label={`Remove ${product.name}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {selectedProducts.length > 0 && (
                      <button
                        type="button"
                        className={styles.clearAllButton}
                        onClick={handleClearAllFilters}
                        aria-label="Clear all filters"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}

                {/* Search Input */}
                <div className={styles.searchInputWrapper}>
                  <Search size={16} className={styles.searchIcon} />
                  <input
                    id="product-filter"
                    type="text"
                    placeholder={
                      selectedProducts.length > 0
                        ? 'Add more products...'
                        : 'Search for products...'
                    }
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                      setTimeout(() => setIsSearchFocused(false), 200);
                    }}
                    className={styles.searchInput}
                    autoComplete="off"
                  />
                </div>

                {/* Product Suggestions */}
                {(isSearchFocused || productSearchQuery.trim()) &&
                  filteredProducts.length > 0 && (
                    <div className={styles.productSuggestions}>
                      {filteredProducts.slice(0, 10).map((product) => (
                        <button
                          key={product.id}
                          className={styles.productSuggestionItem}
                          onClick={() => handleSelectProduct(product.id)}
                          type="button"
                        >
                          <div className={styles.productSuggestionName}>
                            {product.name}
                          </div>
                        </button>
                      ))}
                      {filteredProducts.length > 10 && (
                        <div className={styles.moreResults}>
                          {filteredProducts.length - 10} more product
                          {filteredProducts.length - 10 !== 1 ? 's' : ''}{' '}
                          available
                        </div>
                      )}
                    </div>
                  )}
                {isSearchFocused &&
                  productSearchQuery.trim() &&
                  filteredProducts.length === 0 && (
                    <div className={styles.productSuggestions}>
                      <div className={styles.noResults}>
                        {selectedProducts.length > 0
                          ? 'All available products have been selected'
                          : `No products found matching "${productSearchQuery}"`}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className={styles.summary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Entries:</span>
                <span className={styles.summaryValue}>
                  {filteredData.length}
                  {selectedProductIds.size > 0 &&
                    data.length !== filteredData.length && (
                      <span className={styles.summaryNote}>
                        {' '}
                        (of {data.length} total)
                      </span>
                    )}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Products:</span>
                <span className={styles.summaryValue}>
                  {selectedProductIds.size > 0
                    ? selectedProductIds.size
                    : new Set(data.map((entry) => entry.product.id)).size}
                </span>
              </div>
            </div>

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={styles.noResultsCell}>
                        No entries found for the selected product
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((entry) => {
                      const quantityChange =
                        parseFloat(entry.quantityChange) || 0;
                      const dateTime = formatDate(entry.createdAt);

                      return (
                        <tr
                          key={entry.id}
                          className={entry.isVoided ? styles.voidedRow : ''}
                        >
                          <td className={styles.dateTimeCell}>
                            <div className={styles.dateTimeContainer}>
                              <div className={styles.datePart}>
                                {dateTime.date}
                              </div>
                              <div className={styles.timePart}>
                                {dateTime.time}
                              </div>
                            </div>
                          </td>
                          <td className={styles.productCell}>
                            {entry.product.name || 'Unknown Product'}
                          </td>
                          <td>
                            <span
                              className={`${
                                styles.changeTypeBadge
                              } ${getChangeTypeClass(entry.changeType)}`}
                            >
                              {getChangeTypeLabel(entry.changeType)}
                            </span>
                          </td>
                          <td className={styles.quantityCell}>
                            <span
                              className={
                                quantityChange >= 0
                                  ? styles.positiveChange
                                  : styles.negativeChange
                              }
                            >
                              {quantityChange >= 0 ? '+' : ''}
                              {entry.quantityChange}
                            </span>
                          </td>
                          <td className={styles.quantityCell}>
                            {entry.remainingQuantity}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InventoryReport;
